import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Eye, Lock, Loader2, RefreshCw, KeySquare, SlidersHorizontal, Search, ScrollText, Download, Radio } from "lucide-react";
import { useNavigate } from "react-router-dom";

const MODES = [
  { key: "normal", icon: ShieldCheck, tone: "emerald" },
  { key: "readonly", icon: Eye, tone: "amber" },
  { key: "lockdown", icon: Lock, tone: "red" },
];
const TONE = {
  emerald: "border-emerald-400 bg-emerald-50 text-emerald-700",
  amber: "border-amber-400 bg-amber-50 text-amber-700",
  red: "border-red-400 bg-red-50 text-red-700",
};
const DURATIONS = [0, 15, 30, 60, 240, 1440];
const LEVEL_CLS = {
  ERROR: "text-red-600 dark:text-red-400", CRITICAL: "text-red-700 font-bold dark:text-red-400",
  WARNING: "text-amber-600 dark:text-amber-400", INFO: "text-zinc-600 dark:text-zinc-300", DEBUG: "text-zinc-400 dark:text-zinc-500",
};

function AccessControl() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [pending, setPending] = useState("normal");
  const [minutes, setMinutes] = useState("0");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/superadmin/access-mode").then((r) => {
    setState(r.data); setPending(r.data.mode); setMessage(r.data.message || "");
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/access-mode", {
        mode: pending, minutes: pending === "normal" ? null : Number(minutes), message: pending === "normal" ? null : message,
      });
      setState(data); setMessage(data.message || "");
      const me = await api.get("/auth/me"); setUser(me.data);
      toast.success(t(`sa.applied.${pending}`));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const current = state?.mode || "normal";
  return (
    <div className="space-y-8">
      <div>
        <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
          <ShieldCheck size={14} /> {t("sa.accessControl")}
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {MODES.map((m) => {
            const selected = pending === m.key;
            const active = current === m.key;
            return (
              <button key={m.key} onClick={() => setPending(m.key)} data-testid={`sa-mode-${m.key}`}
                className={`flex flex-col items-start gap-3 border-2 p-5 text-left transition-all ${
                  selected ? TONE[m.tone] : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50"}`}>
                <div className="flex w-full items-center justify-between">
                  <m.icon size={22} />
                  {active && <span className="font-mono text-[10px] font-bold uppercase tracking-wider">{t("sa.active")}</span>}
                </div>
                <div className="font-display text-lg font-black uppercase tracking-tight">{t(`sa.${m.key}`)}</div>
                <p className="text-sm leading-relaxed opacity-90">{t(`sa.${m.key}Desc`)}</p>
              </button>
            );
          })}
        </div>
      </div>

      {pending !== "normal" && (
        <div className="grid gap-4 border border-zinc-200 bg-white p-5 md:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-zinc-500">{t("sa.duration")}</label>
            <Select value={String(minutes)} onValueChange={setMinutes}>
              <SelectTrigger className="rounded-sm border-zinc-300" data-testid="sa-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d === 0 ? t("sa.durNone") : t(`sa.dur${d}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-zinc-500">{t("sa.message")}</label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("sa.messagePh")}
              data-testid="sa-message" rows={2} className="rounded-sm border-zinc-300" />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <button onClick={apply} disabled={busy} data-testid="sa-apply"
          className="flex items-center gap-2 bg-zinc-900 px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-zinc-700 disabled:opacity-50">
          {busy && <Loader2 size={15} className="animate-spin" />} {t("sa.apply")}
        </button>
        {state && (
          <div className="font-mono text-[11px] text-zinc-400" data-testid="sa-current">
            {t("sa.currentMode")}: <span className="text-zinc-700">{t(`sa.${current}`)}</span>
            {state.expiresAt && <> · {t("sa.expiresAt")}: {new Date(state.expiresAt).toLocaleString()}</>}
            {state.updatedBy && <> · {state.updatedBy}</>}
          </div>
        )}
      </div>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="border border-zinc-200 bg-white p-5">
          <div className="mb-2 flex items-center gap-2 font-semibold text-zinc-800"><KeySquare size={17} /> {t("sa.licTitle")}</div>
          <p className="mb-4 text-sm text-zinc-500">{t("sa.licDesc")}</p>
          <button onClick={() => nav("/settings")} data-testid="sa-open-settings"
            className="flex items-center gap-1.5 border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-600 hover:bg-zinc-50">
            <SlidersHorizontal size={14} /> {t("sa.openSettings")}
          </button>
        </div>
        <div className="border border-zinc-200 bg-white p-5">
          <div className="mb-2 flex items-center gap-2 font-semibold text-zinc-800"><ShieldCheck size={17} /> {t("sa.pwTitle")}</div>
          <p className="mb-4 text-sm text-zinc-500">{t("sa.pwDesc")}</p>
          <button onClick={() => nav("/preferences")} data-testid="sa-open-preferences"
            className="flex items-center gap-1.5 border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-600 hover:bg-zinc-50">
            <SlidersHorizontal size={14} /> {t("sa.openPreferences")}
          </button>
        </div>
      </section>
    </div>
  );
}

function DebugLogs() {
  const { t } = useLang();
  const [view, setView] = useState("app");
  const [level, setLevel] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ rows: [], total: 0, retentionDays: 30 });
  const [stream, setStream] = useState("stderr");
  const [lines, setLines] = useState("500");
  const [container, setContainer] = useState({ lines: [] });
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(false);
  const preRef = useRef(null);

  const loadApp = useCallback(() => {
    setLoading(true);
    api.get(`/superadmin/logs?level=${level}&q=${encodeURIComponent(q)}&limit=300`)
      .then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [level, q]);
  const loadContainer = useCallback(() => {
    setLoading(true);
    api.get(`/superadmin/logs/container?stream=${stream}&lines=${lines}`)
      .then((r) => setContainer(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [stream, lines]);

  useEffect(() => { view === "app" ? loadApp() : loadContainer(); }, [view, loadApp, loadContainer]);

  useEffect(() => {
    if (!live || view !== "container") return;
    const id = setInterval(loadContainer, 3000);
    return () => clearInterval(id);
  }, [live, view, loadContainer]);

  useEffect(() => {
    if (view === "container" && preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [container, view]);

  const downloadCurrent = () => {
    let name, content, mime;
    if (view === "app") {
      const esc = (v) => { const s = v == null ? "" : String(v).replace(/\n/g, " "); return /[",]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
      const head = ["Time", "Level", "Logger", "Message", "Exception"];
      const rows = data.rows.map((r) => [r.ts || r.created_at, r.level, r.logger, r.message, r.exception || ""].map(esc).join(","));
      content = [head.join(","), ...rows].join("\n"); name = "debug-logs.csv"; mime = "text/csv;charset=utf-8;";
    } else {
      content = (container.lines || []).join("\n"); name = `backend-${stream}.log`; mime = "text/plain;charset=utf-8;";
    }
    const blob = new Blob(["\ufeff" + content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const lineCls = (ln) => {
    if (/error|traceback|exception|critical|fail/i.test(ln)) return "text-red-400";
    if (/warn/i.test(ln)) return "text-amber-300";
    return "text-zinc-100";
  };
  const btnCls = "flex items-center gap-1.5 border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex border border-zinc-200">
          <button onClick={() => setView("app")} data-testid="sa-logs-app"
            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider ${view === "app" ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 hover:bg-zinc-100"}`}>{t("sa.appLogs")}</button>
          <button onClick={() => setView("container")} data-testid="sa-logs-container"
            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider ${view === "container" ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 hover:bg-zinc-100"}`}>{t("sa.containerLogs")}</button>
        </div>
        {view === "app" ? (
          <>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="w-36 rounded-sm border-zinc-300 text-sm" data-testid="sa-log-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["all", "INFO", "WARNING", "ERROR"].map((l) => <SelectItem key={l} value={l}>{l === "all" ? t("filter.allStatuses") : l}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative w-64 max-w-full">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadApp()}
                placeholder={t("sa.searchPh")} data-testid="sa-log-search" className="rounded-sm border-zinc-300 pl-9" />
            </div>
            <button onClick={loadApp} data-testid="sa-log-refresh" className={btnCls}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {t("sa.refresh")}
            </button>
            <button onClick={downloadCurrent} data-testid="sa-log-download" className={btnCls}>
              <Download size={14} /> {t("sa.download")}
            </button>
            <span className="ml-auto font-mono text-[11px] text-zinc-400">{data.retentionDays}d {t("sa.retention")}</span>
          </>
        ) : (
          <>
            <Select value={stream} onValueChange={setStream}>
              <SelectTrigger className="w-36 rounded-sm border-zinc-300 text-sm" data-testid="sa-log-stream"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="stderr">stderr</SelectItem><SelectItem value="stdout">stdout</SelectItem></SelectContent>
            </Select>
            <Select value={lines} onValueChange={setLines}>
              <SelectTrigger className="w-28 rounded-sm border-zinc-300 text-sm" data-testid="sa-log-lines"><SelectValue /></SelectTrigger>
              <SelectContent>{["200", "500", "1000", "2000"].map((n) => <SelectItem key={n} value={n}>{n} {t("sa.lines")}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={loadContainer} data-testid="sa-log-refresh" className={btnCls}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {t("sa.refresh")}
            </button>
            <button onClick={() => setLive((v) => !v)} data-testid="sa-log-live"
              className={`flex items-center gap-1.5 border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
                live ? "border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" : btnCls}`}>
              <Radio size={14} className={live ? "animate-pulse" : ""} /> {live ? t("sa.liveOn") : t("sa.live")}
            </button>
            <button onClick={downloadCurrent} data-testid="sa-log-download" className={btnCls}>
              <Download size={14} /> {t("sa.download")}
            </button>
          </>
        )}
      </div>

      {view === "app" ? (
        <div className="max-h-[62vh] overflow-auto border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900" data-testid="sa-app-logs">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
              <tr><th className="px-3 py-2">Time</th><th className="px-3 py-2">Level</th><th className="px-3 py-2">Logger</th><th className="px-3 py-2">Message</th></tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={4} className="px-3 py-10 text-center text-zinc-400">{t("sa.noLogs")}</td></tr>}
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-zinc-100 align-top hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-800/60">
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[11px] text-zinc-400 dark:text-zinc-500">{new Date(r.created_at || r.ts).toLocaleTimeString()}</td>
                  <td className={`px-3 py-1.5 font-mono text-[11px] font-bold ${LEVEL_CLS[r.level] || "text-zinc-600 dark:text-zinc-300"}`}>{r.level}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[11px] text-zinc-500 dark:text-zinc-400">{r.logger}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-700 dark:text-zinc-200">
                    {r.message}
                    {r.exception && <pre className="mt-1 whitespace-pre-wrap rounded-sm bg-red-50 p-2 text-[10px] text-red-700 dark:bg-red-500/10 dark:text-red-300">{r.exception}</pre>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={preRef} className="max-h-[62vh] overflow-auto border border-zinc-800 bg-zinc-950 p-4" data-testid="sa-container-logs">
          {container.error && <div className="text-xs text-red-400">{container.error}</div>}
          {(container.lines || []).length === 0 && !container.error && <div className="text-xs text-zinc-400">{t("sa.noLogs")}</div>}
          <pre className="font-mono text-[11px] leading-relaxed">
            {(container.lines || []).map((ln, i) => (
              <div key={i} className={`whitespace-pre-wrap ${lineCls(ln)}`}>{ln}</div>
            ))}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function SuperAdmin() {
  const { t } = useLang();
  return (
    <>
      <PageHeader title={t("sa.title")} subtitle={t("sa.subtitle")} />
      <div className="p-6 md:p-10">
        <Tabs defaultValue="access">
          <TabsList className="mb-6">
            <TabsTrigger value="access" data-testid="sa-tab-access">{t("sa.tabAccess")}</TabsTrigger>
            <TabsTrigger value="logs" data-testid="sa-tab-logs"><ScrollText size={14} className="mr-1.5" /> {t("sa.tabLogs")}</TabsTrigger>
          </TabsList>
          <TabsContent value="access"><AccessControl /></TabsContent>
          <TabsContent value="logs"><DebugLogs /></TabsContent>
        </Tabs>
      </div>
    </>
  );
}
